import { useState } from 'react';
import { DocumentIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import type { ClubTheme } from './themes';
import PdfViewerModal from './PdfViewerModal';

interface PdfAttachmentProps {
  url: string;
  filename: string;
  size?: number;
  theme: ClubTheme;
}

function humanSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PdfAttachment({ url, filename, size, theme }: PdfAttachmentProps) {
  const [viewerOpen, setViewerOpen] = useState(false);

  return (
    <>
      <div
        className="mt-3 flex items-center gap-3 rounded-xl p-3 cursor-pointer transition-opacity hover:opacity-80"
        style={{ backgroundColor: `${theme.colors.muted}10`, border: `1px solid ${theme.colors.muted}20` }}
        onClick={() => setViewerOpen(true)}
      >
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${theme.colors.primary}20` }}
        >
          <DocumentIcon className="h-5 w-5" style={{ color: theme.colors.primary }} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate" style={{ color: theme.colors.text }}>
            {filename}
          </p>
          {size != null && size > 0 && (
            <p className="text-[11px]" style={{ color: theme.colors.muted }}>
              {humanSize(size)} &middot; PDF
            </p>
          )}
        </div>

        <a
          href={url}
          download={filename}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 rounded-lg p-1.5 transition-colors hover:opacity-70"
          style={{ color: theme.colors.muted }}
          title="Download"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
        </a>
      </div>

      {viewerOpen && (
        <PdfViewerModal url={url} filename={filename} theme={theme} onClose={() => setViewerOpen(false)} />
      )}
    </>
  );
}
